import { useEffect, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this account?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Account removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell active="/users">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-carmel-gold mb-2">Access</p>
          <h2 className="font-serif text-2xl md:text-4xl tracking-tight">Staff Accounts</h2>
          <p className="text-sm text-carmel-navy/60 mt-2 max-w-2xl">
            Only you (the admin) issue and manage staff logins. <span className="font-medium text-carmel-navy">Editors</span> can record inspections; <span className="font-medium text-carmel-navy">Viewers</span> can only see records. Staff cannot change their own credentials — use the Edit button here to reset any password.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-carmel-gold hover:bg-carmel-goldDark" data-testid="add-user-open"><UserPlus className="w-4 h-4 mr-2" /> New Staff Account</Button>
          </DialogTrigger>
          <NewUser onDone={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      <div className="bg-white border border-carmel-navy/10 rounded-lg overflow-x-auto shadow-card">
        <Table>
          <TableHeader className="bg-carmel-slateBg">
            <TableRow>
              <TableHead className="uppercase text-xs tracking-wider">Name</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Email</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Role</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Added</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="hover:bg-carmel-slateBg" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-carmel-navy/70">{u.email}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    u.role === "admin" ? "bg-carmel-gold/10 text-carmel-goldDark border-carmel-gold/30" :
                    u.role === "editor" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    "bg-slate-100 text-slate-700 border-slate-200"
                  }>
                    <Shield className="w-3 h-3 mr-1" /> {u.role}
                  </Badge>
                </TableCell>
                <TableCell className="text-carmel-navy/60 text-xs">{u.created_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditUser(u)} data-testid={`user-edit-${u.id}`}>
                      <Pencil className="w-4 h-4 text-carmel-navy/70" />
                    </Button>
                    {u.role !== "admin" && (
                      <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`user-delete-${u.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        {editUser && (
          <EditUser
            user={editUser}
            isSelf={editUser.id === me?.id}
            onDone={() => { setEditUser(null); load(); }}
          />
        )}
      </Dialog>
    </AppShell>
  );
}

function EditUser({ user, isSelf, onDone }) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(user.role);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const payload = {};
    if (name.trim() && name !== user.name) payload.name = name.trim();
    if (email.trim().toLowerCase() !== user.email) payload.email = email.trim().toLowerCase();
    if (!isSelf && role !== user.role) payload.role = role;
    if (password) {
      if (password.length < 6) { return toast.error("Password must be ≥ 6 chars"); }
      payload.password = password;
    }
    if (Object.keys(payload).length === 0) return toast.info("No changes to save");
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, payload);
      toast.success(`${user.name} updated`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <DialogContent>
      <DialogHeader><DialogTitle className="font-serif text-2xl">Edit Account — {user.name}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div><Label>Display Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" className="mt-1" /></div>
        <div><Label>Login Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-user-email" className="mt-1" /></div>
        <div>
          <Label>New Password (leave blank to keep current)</Label>
          <Input type="text" placeholder="••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="edit-user-password" className="mt-1" />
        </div>
        {!isSelf && (
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="edit-user-role" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {isSelf && (
          <p className="text-xs text-carmel-navy/60 italic">You can't change your own role from here — it's locked to prevent self-lockout.</p>
        )}
      </div>
      <DialogFooter>
        <Button className="bg-carmel-gold hover:bg-carmel-goldDark" onClick={save} disabled={busy} data-testid="edit-user-save">Save changes</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function NewUser({ onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name || !email || !password) return toast.error("All fields required");
    setBusy(true);
    try {
      await api.post("/users", { name, email: email.toLowerCase(), password, role });
      toast.success(`Account created for ${name}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <DialogContent>
      <DialogHeader><DialogTitle className="font-serif text-2xl">New Staff Account</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div><Label>Full Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name" className="mt-1" /></div>
        <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email" className="mt-1" /></div>
        <div><Label>Temporary Password</Label><Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-user-password" className="mt-1" /></div>
        <div>
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="new-user-role" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="editor">Editor — record inspections & manage students</SelectItem>
              <SelectItem value="viewer">Viewer — read-only records + export</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button className="bg-carmel-gold hover:bg-carmel-goldDark" onClick={save} disabled={busy} data-testid="new-user-save">Create Account</Button>
      </DialogFooter>
    </DialogContent>
  );
}
